import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Briefcase, User, Send, Star, RefreshCw, Mail, Phone, MapPin, ArrowRight, Eye, TrendingUp, Trash2, Flame, Megaphone, StopCircle, Bell, Target, Zap } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ts, setTs] = useState(new Date());
  const [showTests, setShowTests] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [boost, setBoost] = useState(null);
  const [boostBusy, setBoostBusy] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertsBusy, setAlertsBusy] = useState(false);
  const [heatmap, setHeatmap] = useState(null);

  const loadHeatmap = async () => {
    try {
      const { data } = await api.get("/admin/heatmap");
      setHeatmap(data);
    } catch (e) { console.error("[Admin] heatmap fetch failed", e); }
  };

  const loadAlerts = async () => {
    try {
      const { data } = await api.get("/admin/signup-alerts");
      setAlerts(data);
      setAlertEmail(data.recipient || "");
    } catch (e) { console.error("[Admin] signup-alerts fetch failed", e); }
  };

  const saveAlerts = async () => {
    if (!alertEmail || !alertEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setAlertsBusy(true);
    try {
      await api.post("/admin/signup-alerts", { recipient: alertEmail.trim().toLowerCase() });
      toast.success("Signup alerts will now go to " + alertEmail);
      await loadAlerts();
    } catch (e) { toast.error("Save failed"); }
    finally { setAlertsBusy(false); }
  };

  const testAlert = async () => {
    setAlertsBusy(true);
    try {
      const { data } = await api.post("/admin/signup-alerts/test");
      toast.success(data.ok ? `Test alert sent to ${data.recipient}` : `Send failed to ${data.recipient} — check Resend`);
    } catch (e) { toast.error("Test failed"); }
    finally { setAlertsBusy(false); }
  };

  const sendBulletinNow = async () => {
    if (!window.confirm("Send the Monday Bulletin (7-day recap + heatmap) to the alerts recipient right now?")) return;
    setAlertsBusy(true);
    try {
      const { data } = await api.post("/admin/bulletin/send-now");
      toast.success(data.ok ? `Bulletin sent to ${data.recipient}` : `Not sent (${data.reason || "failed"})`);
    } catch (e) { toast.error("Bulletin send failed"); }
    finally { setAlertsBusy(false); }
  };

  const loadBoost = async () => {
    try {
      const { data } = await api.get("/referrals/boost");
      setBoost(data);
    } catch (e) {
      console.error("[Admin] boost fetch failed", e);
    }
  };

  const startBoost = async (hours = 48, multiplier = 2) => {
    if (!window.confirm(`Turn ON €${10 * multiplier} double bounty for ${hours}h?`)) return;
    setBoostBusy(true);
    try {
      const { data } = await api.post(`/admin/referral-boost/start?hours=${hours}&multiplier=${multiplier}`);
      setBoost(data);
      toast.success(`Boost live — €${data.prize_eur}/signup for ${data.hours_left}h`);
    } catch (e) {
      toast.error("Could not start boost");
    } finally { setBoostBusy(false); }
  };

  const stopBoost = async () => {
    if (!window.confirm("Stop the referral boost now?")) return;
    setBoostBusy(true);
    try {
      const { data } = await api.post("/admin/referral-boost/stop");
      setBoost(data);
      toast.success("Boost stopped");
    } catch (e) {
      toast.error("Could not stop boost");
    } finally { setBoostBusy(false); }
  };

  const broadcastBoost = async () => {
    if (!window.confirm("Email EVERY real user a nudge to share their referral link now?")) return;
    setBoostBusy(true);
    setBroadcastResult(null);
    try {
      const { data } = await api.post("/admin/referral-boost/broadcast");
      setBroadcastResult(data);
      toast.success(`Nudge sent to ${data.sent}/${data.recipients} users`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBoostBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/stats${showTests ? "?include_tests=true" : ""}`);
      setStats(data);
      setTs(new Date());
    } catch (e) {
      console.error("[Admin] failed to load /admin/stats", e);
    }
    finally { setLoading(false); }
  };

  const purge = async () => {
    if (!window.confirm("Permanently delete ALL test signups (emails matching @example.com or test prefixes) and everything they created? Real users are safe.")) return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const { data } = await api.post("/admin/purge-test-users");
      setPurgeResult(data.deleted);
      await load();
    } catch (e) {
      alert("Purge failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPurging(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      load();
      loadBoost();
      loadAlerts();
      loadHeatmap();
      const t = setInterval(() => { load(); loadBoost(); loadHeatmap(); }, 15000); // auto-refresh every 15s
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showTests]);

  if (user === null) return <div className="p-10 text-neutral-500 uppercase tracking-widest">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Admin</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Live Signups</h1>
          <div className="text-neutral-400 text-sm mt-2 uppercase tracking-wide">
            {showTests ? "Showing ALL (incl. QA tests)" : "Real people only — QA tests hidden"} · Auto-refresh 15s · Last: {ts.toLocaleTimeString()}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-800 text-[11px] uppercase tracking-widest cursor-pointer hover:border-amber-500">
            <input type="checkbox" checked={showTests} onChange={(e)=>setShowTests(e.target.checked)} data-testid="admin-show-tests-toggle" className="accent-amber-500" />
            Show tests
          </label>
          <button
            data-testid="admin-purge-tests"
            onClick={purge}
            disabled={purging}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-900 text-red-400 hover:border-red-500 hover:text-red-300 text-xs uppercase tracking-widest transition-colors disabled:opacity-50"
          >
            <Trash2 className={`w-3 h-3 ${purging ? "animate-pulse" : ""}`} /> {purging ? "Purging…" : "Purge test signups"}
          </button>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-800 hover:border-amber-500 hover:text-amber-500 text-xs uppercase tracking-widest transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh now
          </button>
        </div>
      </div>

      {purgeResult && (
        <div data-testid="admin-purge-result" className="mb-6 border border-red-900 bg-red-950/30 text-red-200 px-4 py-3 text-sm">
          Purged — users: {purgeResult.users} · jobs: {purgeResult.jobs} · worker profiles: {purgeResult.worker_profiles} · applications: {purgeResult.applications} · reveals: {purgeResult.contact_reveals}
        </div>
      )}

      {/* Referral Boost control */}
      <div data-testid="boost-control" className={`mb-8 border-2 ${boost?.active ? "border-amber-500 bg-amber-500/10" : "border-neutral-800 bg-neutral-900"} p-5`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Flame className={`w-5 h-5 ${boost?.active ? "text-amber-500 animate-pulse" : "text-neutral-500"}`} />
              <div className="display text-xl">Referral Boost</div>
              {boost?.active && (
                <span className="text-[10px] uppercase tracking-widest bg-amber-500 text-neutral-950 px-1.5 py-0.5 font-bold">LIVE</span>
              )}
            </div>
            {boost?.active ? (
              <div className="text-sm text-amber-200 mt-2">
                €{boost.prize_eur} per signup · {boost.hours_left}h left · Reverts to €{boost.base_prize_eur || 10}
              </div>
            ) : (
              <div className="text-sm text-neutral-400 mt-2">
                Currently OFF — bounty is €{boost?.base_prize_eur || 10}/signup. Turn on the double bounty and blast every user to share.
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!boost?.active ? (
              <button
                data-testid="boost-start"
                disabled={boostBusy}
                onClick={() => startBoost(48, 2)}
                className="inline-flex items-center gap-2 bg-amber-500 text-neutral-950 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-amber-400 disabled:opacity-50"
              >
                <Flame className="w-3 h-3" /> Start 48h €20 bounty
              </button>
            ) : (
              <>
                <button
                  data-testid="boost-broadcast"
                  disabled={boostBusy}
                  onClick={broadcastBoost}
                  className="inline-flex items-center gap-2 bg-amber-500 text-neutral-950 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-amber-400 disabled:opacity-50"
                >
                  <Megaphone className="w-3 h-3" /> Email all users now
                </button>
                <button
                  data-testid="boost-stop"
                  disabled={boostBusy}
                  onClick={stopBoost}
                  className="inline-flex items-center gap-2 border border-red-900 text-red-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-red-500 disabled:opacity-50"
                >
                  <StopCircle className="w-3 h-3" /> Stop now
                </button>
              </>
            )}
          </div>
        </div>
        {broadcastResult && (
          <div data-testid="boost-broadcast-result" className="mt-3 text-xs uppercase tracking-widest text-amber-200">
            Broadcast → sent {broadcastResult.sent}/{broadcastResult.recipients} · failed {broadcastResult.failed}
          </div>
        )}
      </div>

      {/* Signup Milestone Alerts */}
      <div data-testid="signup-alerts-control" className="mb-8 border border-neutral-800 bg-neutral-900 p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Bell className="w-5 h-5 text-amber-500 shrink-0 mt-1" />
          <div className="min-w-0 flex-1">
            <div className="display text-xl">Signup Milestone Alerts</div>
            <div className="text-sm text-neutral-400 mt-1">
              Get an email the second a real person signs up. QA/test signups are skipped automatically.
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="signup-alert-email"
                type="email"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-neutral-950 border border-neutral-800 text-white px-3 py-2 text-sm focus:border-amber-500 outline-none"
              />
              <button
                data-testid="signup-alert-save"
                onClick={saveAlerts}
                disabled={alertsBusy}
                className="inline-flex items-center justify-center gap-2 bg-amber-500 text-neutral-950 px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-amber-400 disabled:opacity-50"
              >
                Save
              </button>
              <button
                data-testid="signup-alert-test"
                onClick={testAlert}
                disabled={alertsBusy}
                className="inline-flex items-center justify-center gap-2 border border-neutral-800 text-neutral-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-amber-500 hover:text-amber-500 disabled:opacity-50"
              >
                Send test
              </button>
              <button
                data-testid="bulletin-send-now"
                onClick={sendBulletinNow}
                disabled={alertsBusy}
                className="inline-flex items-center justify-center gap-2 border border-neutral-800 text-neutral-300 px-4 py-2 text-xs uppercase tracking-widest hover:border-amber-500 hover:text-amber-500 disabled:opacity-50"
              >
                Send bulletin now
              </button>
            </div>
            <div className="mt-2 text-[11px] uppercase tracking-widest text-neutral-500">
              Currently pinging: <span className="text-neutral-300">{alerts?.recipient || "not set"}</span>
              {alerts && !alerts.custom && <span className="ml-2 text-amber-500">(using default)</span>}
            </div>
          </div>
        </div>
      </div>

      {/* County × Trade Heatmap */}
      {heatmap && (
        <div data-testid="heatmap-card" className="mb-8 grid md:grid-cols-2 gap-4">
          <div className="border border-neutral-800 bg-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-500" />
              <div className="display text-xl">Ad Targets — Biggest Gaps</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 ml-auto">Jobs waiting · no workers</div>
            </div>
            {heatmap.top_gaps.length === 0 ? (
              <div className="p-5 text-sm text-neutral-500">No supply/demand gaps yet — post more jobs to spot patterns.</div>
            ) : (
              <ul className="divide-y divide-neutral-800">
                {heatmap.top_gaps.map((g, i) => (
                  <li key={`${g.county}-${g.trade}`} data-testid={`heatmap-gap-${i}`} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        <span className="text-amber-500 font-bold">{g.trade}</span>
                        <span className="text-neutral-500 mx-1.5">in</span>
                        <span>{g.county}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                        {g.jobs} job{g.jobs === 1 ? "" : "s"} live · {g.workers} worker{g.workers === 1 ? "" : "s"} on board
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="display text-2xl text-amber-500 tabular-nums">+{g.gap}</div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">need more</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border border-neutral-800 bg-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <div className="display text-xl">Hotspots — Where It's Busy</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 ml-auto">Both sides active</div>
            </div>
            {heatmap.top_hotspots.length === 0 ? (
              <div className="p-5 text-sm text-neutral-500">No hotspots yet.</div>
            ) : (
              <ul className="divide-y divide-neutral-800">
                {heatmap.top_hotspots.map((h, i) => (
                  <li key={`hs-${h.county}-${h.trade}`} data-testid={`heatmap-hotspot-${i}`} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">
                        <span className="text-amber-500 font-bold">{h.trade}</span>
                        <span className="text-neutral-500 mx-1.5">·</span>
                        <span>{h.county}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                        {h.jobs} job{h.jobs === 1 ? "" : "s"} · {h.workers} worker{h.workers === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="display text-2xl text-amber-500 tabular-nums">{h.jobs + h.workers}</div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">total</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* County demand bar */}
          <div className="md:col-span-2 border border-neutral-800 bg-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-500" />
              <div className="display text-xl">Opportunity by County</div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 ml-auto">Higher = jobs outnumber workers</div>
            </div>
            <ul className="divide-y divide-neutral-800">
              {heatmap.counties.slice(0, 10).map((c) => {
                const max = Math.max(1, ...heatmap.counties.map((x) => Math.abs(x.opportunity)));
                const pct = Math.min(100, (Math.abs(c.opportunity) / max) * 100);
                const positive = c.opportunity > 0;
                return (
                  <li key={c.county} className="px-5 py-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white font-bold">{c.county}</span>
                      <span className="tabular-nums text-neutral-400 text-xs uppercase tracking-widest">
                        {c.jobs} jobs · {c.workers} workers
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-neutral-950 relative overflow-hidden">
                      <div
                        className={`h-full ${positive ? "bg-amber-500" : "bg-neutral-700"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {!stats ? (
        <div className="text-neutral-500 text-sm uppercase tracking-widest animate-pulse">Loading stats…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <BigStat label="Total Signups" main={stats.users.total} delta={stats.users.last_24h} Icon={Users} />
            <BigStat label="Jobs Posted" main={stats.jobs.total} delta={stats.jobs.last_24h} Icon={Briefcase} />
            <BigStat label="Worker Profiles" main={stats.worker_profiles.total} delta={null} Icon={User} />
            <BigStat label="Applications" main={stats.applications.total} delta={stats.applications.last_24h} Icon={Send} />
          </div>

          {stats.traffic && (
            <div className="border border-neutral-800 bg-neutral-900 mb-8">
              <div className="px-5 py-4 border-b border-neutral-800 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-500" />
                <div className="display text-xl">Site traffic</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 ml-auto">Anonymous · Privacy-friendly</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-neutral-800">
                <TrafficStat label="Last hour" value={stats.traffic.last_hour} />
                <TrafficStat label="Last 24h" value={stats.traffic.last_24h} />
                <TrafficStat label="Unique visitors 24h" value={stats.traffic.unique_visitors_24h} />
                <TrafficStat label="All-time views" value={stats.traffic.total} />
              </div>
              {stats.traffic.top_paths_24h?.length > 0 && (
                <div className="border-t border-neutral-800 px-5 py-4">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Most-viewed pages · Last 24h</div>
                  <ul className="space-y-1">
                    {stats.traffic.top_paths_24h.map((p) => (
                      <li key={p.path} className="flex items-center justify-between text-sm">
                        <span className="text-neutral-200 font-mono truncate">{p.path}</span>
                        <span className="text-amber-500 tabular-nums">{p.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <MiniStat label="Employers" value={stats.users.employers} />
            <MiniStat label="Tradespeople" value={stats.users.workers} />
            <MiniStat label="Reviews" value={stats.reviews.total} Icon={Star} />
          </div>

          <div className="border border-neutral-800 bg-neutral-900">
            <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
              <div className="display text-xl">Latest Signups</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Newest 10</div>
            </div>
            {stats.latest_users.length === 0 ? (
              <div className="p-6 text-sm text-neutral-500">No signups yet — share the link and check back in a bit.</div>
            ) : (
              <ul className="divide-y divide-neutral-800">
                {stats.latest_users.map((u) => (
                  <li key={u.email} data-testid={`admin-user-${u.email}`} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="display text-lg">{u.name}</span>
                        <span className="text-[10px] uppercase tracking-widest bg-amber-500 text-neutral-950 px-1.5 py-0.5 font-bold">{u.role}</span>
                        {u.county && <span className="text-[10px] uppercase tracking-widest text-neutral-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{u.county}</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-neutral-300">
                        <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1 hover:text-amber-500"><Mail className="w-3 h-3" />{u.email}</a>
                        {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 hover:text-amber-500"><Phone className="w-3 h-3" />{u.phone}</a>}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 shrink-0">
                      {u.created_at ? new Date(u.created_at).toLocaleString() : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/jobs" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500">Browse all jobs <ArrowRight className="w-3 h-3" /></Link>
            <span className="text-neutral-700">·</span>
            <Link to="/workers" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-neutral-400 hover:text-amber-500">Browse all workers <ArrowRight className="w-3 h-3" /></Link>
          </div>
        </>
      )}
    </div>
  );
}

function BigStat({ label, main, delta, Icon }) {
  return (
    <div className="border border-neutral-800 bg-neutral-900 p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-neutral-400">{label}</div>
        <Icon className="w-4 h-4 text-amber-500" />
      </div>
      <div className="display text-5xl text-amber-500 mt-2">{String(main).padStart(2, "0")}</div>
      {delta !== null && delta !== undefined && (
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
          +{delta} in last 24h
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, Icon }) {
  return (
    <div className="border border-neutral-800 bg-neutral-900 p-4 flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="display text-3xl text-amber-500 mt-1">{value}</div>
      </div>
      {Icon && <Icon className="w-4 h-4 text-amber-500" />}
    </div>
  );
}

function TrafficStat({ label, value }) {
  return (
    <div className="p-5">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="display text-3xl text-amber-500 mt-1 tabular-nums">{value}</div>
    </div>
  );
}
